import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';

// --- Helper Components ---

// A simple modal for confirming deletion
const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Action</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

// A simple message/alert box
const AlertMessage = ({ message, type = 'error' }) => {
    if (!message) return null;
    const colors = {
        error: 'bg-red-100 border-red-500 text-red-700',
        success: 'bg-green-100 border-green-500 text-green-700',
    };
    return (
        <div className={`border-l-4 p-4 my-4 ${colors[type]}`} role="alert">
            <p>{message}</p>
        </div>
    );
};


// --- Main Component ---

/**
 * A React component to manage a user's registered biometric credentials.
 * It fetches, displays, and allows deletion of credentials.
 * * @param {object} props
 * @param {object} props.functions - The Firebase Functions instance from your app's initialization.
 */
const BiometricSettings = ({ functions }) => {
    const [credentials, setCredentials] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [credentialToDelete, setCredentialToDelete] = useState(null);

    const fetchCredentials = useCallback(async () => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            if (!functions) {
                throw new Error("Firebase Functions instance is not provided.");
            }
            const getRegisteredCredentials = httpsCallable(functions, 'getRegisteredCredentials');
            const result = await getRegisteredCredentials();
            setCredentials(result.data);
        } catch (err) {
            console.error("Error fetching credentials:", err);
            const errorMessage = err.message || 'Could not fetch registered devices.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [functions]);

    useEffect(() => {
        fetchCredentials();
    }, [fetchCredentials]);

    const handleDelete = async () => {
        if (!credentialToDelete) return;
        
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const deleteCredential = httpsCallable(functions, 'deleteCredential');
            await deleteCredential({ credentialID: credentialToDelete });
            setSuccessMessage('Biometric login successfully removed.');
            await fetchCredentials();
        } catch (err) {
            console.error("Error deleting credential:", err);
            setError(err.message || 'Failed to delete the biometric login.');
        } finally {
            setIsLoading(false);
            closeModal();
        }
    };
    
    const openModal = (credentialID) => {
        setCredentialToDelete(credentialID);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setCredentialToDelete(null);
        setIsModalOpen(false);
    };


    return (
        <div className="max-w-2xl mx-auto p-4">
            <div className="bg-white shadow-md rounded-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Manage Biometric Login</h2>
                <p className="text-gray-600 mb-6">Here are the devices you have registered for biometric login. You can remove any you no longer use.</p>

                <AlertMessage message={error} type="error" />
                <AlertMessage message={successMessage} type="success" />

                {isLoading && <p className="text-gray-500">Loading devices...</p>}

                {!isLoading && credentials.length === 0 && (
                    <div className="text-center py-6 bg-gray-50 rounded-md">
                        <p className="text-gray-600">No biometric logins are registered.</p>
                    </div>
                )}

                {!isLoading && credentials.length > 0 && (
                    <ul className="space-y-4">
                        {credentials.map(cred => (
                            <li key={cred.credentialID} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                                <div>
                                    {/* FIX: Display the device name */}
                                    <p className="font-semibold text-gray-700">{cred.deviceName}</p>
                                    <p className="text-sm text-gray-500">
                                        Added on: {new Date(cred.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                                <button
                                    onClick={() => openModal(cred.credentialID)}
                                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Delete
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this biometric login? This action cannot be undone."
            />
        </div>
    );
};

export default BiometricSettings;